import {
  AUDIT_CATEGORY_LABELS,
  CELL_ACCESS_LABELS,
  FIELD_RESTRICTIONS,
  PERMISSION_OBJECTS,
  PERMISSION_OBJECT_COPY,
  RESTRICTED_TRANSITION_LABELS,
  ROLE_KEYS,
  ROLE_LABELS,
  ROLE_SCOPE_KIND_LABELS,
  answersOfCell,
  describeDevice,
  isAccountAttempt,
  reachWording,
  type AuditCategory,
  type AuditEntry,
  type PermissionLevel,
  type PermissionObject,
  type RestrictedTransition,
  type RoleKey,
  type RoleScopeKind,
} from '@nest-recruit/shared';
import { EMPTY, displayTimeZone, formatDate, toSentenceCase } from '../../../../lib/format';

// USER-06 in plain words (Admin redesign 2026-11-02, Access log A): the line each entry reads as in the feed, the
// day it is grouped under, and the full story on the right — before → after for role changes, who, address and
// device. An audit nobody can read is an audit nobody reads.

/** Names the entry itself doesn't carry: roles by id, people by id, ventures and departments by id. */
export interface AccessLogLookups {
  readonly roleLabelOf: (roleId: string) => string | null;
  /** The standard role a role is, or was copied from, for "their own" wording. */
  readonly roleKeyOf: (roleId: string) => RoleKey | null;
  readonly personNameOf: (userId: string) => string | null;
  readonly placeNameOf: (id: string) => string | null;
}

export const NO_LOOKUPS: AccessLogLookups = {
  roleLabelOf: () => null,
  roleKeyOf: () => null,
  personNameOf: () => null,
  placeNameOf: () => null,
};

type Record_ = Readonly<Record<string, unknown>>;

function recordOf(value: unknown): Record_ {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record_) : {};
}

function stringOf(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function metadataOf(entry: AuditEntry): Record_ {
  return recordOf(entry.metadata);
}

function actorOf(entry: AuditEntry): string {
  return entry.actorName ?? (entry.actorType === 'system' ? 'The system' : 'Someone');
}

function personOf(entry: AuditEntry): string {
  return entry.personName ?? 'someone';
}

/** The account an attempt was made on: its owner's name, or the address typed, never the attempt's maker. */
function attemptedAccountOf(entry: AuditEntry, metadata: Record_): string {
  return entry.personName ?? stringOf(metadata.email) ?? 'an account';
}

function isPermissionObject(value: unknown): value is PermissionObject {
  return typeof value === 'string' && (PERMISSION_OBJECTS as readonly string[]).includes(value);
}

function objectLabel(value: unknown): string {
  if (isPermissionObject(value)) return PERMISSION_OBJECT_COPY[value].label;
  return typeof value === 'string' ? toSentenceCase(value.replace(/_/g, ' ')) : 'something';
}

function roleNamed(entry: AuditEntry): string {
  return entry.roleLabel ? `the ${entry.roleLabel} role` : 'a role';
}

// --- The headline -----------------------------------------------------------------------------

/** The entry as one sentence: "Nasreen Akter changed the Recruiter role". */
export function headlineOf(entry: AuditEntry): string {
  const actor = actorOf(entry);
  const metadata = metadataOf(entry);
  const after = recordOf(entry.payloadAfter);
  switch (entry.eventType) {
    case 'auth.login_success':
      return `${actor} signed in`;
    case 'auth.logout':
      return `${actor} signed out`;
    // Review R21: whoever typed the password is unknown, so an attempt is never "done by" the account's owner.
    case 'auth.login_failed':
      return `Someone tried to sign in as ${attemptedAccountOf(entry, metadata)}`;
    case 'auth.2fa_failed':
      return `Someone tried to sign in as ${attemptedAccountOf(entry, metadata)} with a wrong two-factor code`;
    case 'auth.permission_denied':
      return `${actor} was refused ${objectLabel(metadata.object)}`;
    case 'auth.impersonation_started':
      return `${actor} viewed the app as ${stringOf(metadata.subjectName) ?? personOf(entry)}`;
    case 'auth.impersonation_ended':
      return `${actor} stopped viewing as ${stringOf(metadata.subjectName) ?? personOf(entry)}`;
    case 'user.role_assigned':
      return 'assignment' in after ? `${actor} gave ${personOf(entry)} their first role` : `${actor} changed ${personOf(entry)}'s roles`;
    case 'user.role_removed':
      return `${actor} removed a role from ${personOf(entry)}`;
    case 'role.created':
      return `${actor} created the role "${stringOf(after.label) ?? entry.roleLabel ?? ''}"`;
    case 'role.updated':
      return 'label' in after ? `${actor} renamed ${roleNamed(entry)}` : `${actor} changed ${roleNamed(entry)}`;
    case 'role.deleted':
      return `${actor} deleted the role "${stringOf(recordOf(entry.payloadBefore).label) ?? entry.roleLabel ?? ''}"`;
    case 'role.field_rule_changed':
      return `${actor} changed what ${entry.roleLabel ?? 'a role'} can see`;
    case 'role.transition_rule_changed':
      return `${actor} changed the special actions of ${roleNamed(entry)}`;
    case 'delegation.created':
      return `${actor} arranged cover`;
    case 'delegation.ended':
      return `${actor} ended a cover`;
    case 'delegation.dates_changed':
      return `${actor} changed the dates of a cover`;
    case 'delegation.restored':
      return `${actor} brought back a cover they had ended`;
    case 'user.invited':
      return `${actor} invited ${stringOf(metadata.email) ?? personOf(entry)}`;
    case 'user.invite_resent':
      return `${actor} sent ${personOf(entry)}'s invite again`;
    case 'user.invite_revoked':
      return `${actor} cancelled ${personOf(entry)}'s invite`;
    case 'user.activated':
      return `${entry.personName ?? actor} accepted their invite`;
    case 'user.updated':
      return `${actor} edited ${personOf(entry)}'s details`;
    case 'user.deactivated':
      return `${actor} deactivated ${personOf(entry)}`;
    case 'user.reactivated':
      return `${actor} reactivated ${personOf(entry)}`;
    case 'user.suspended':
      return `${actor} suspended ${personOf(entry)}`;
    case 'user.unsuspended':
      return `${actor} lifted ${personOf(entry)}'s suspension`;
    case 'user.password_reset_sent':
      return `${actor} sent ${personOf(entry)} a password reset link`;
    case 'ownership.transferred':
      return `${actor} handed work over`;
    case 'auth.password_changed':
      return `${actor} changed their password`;
    case 'auth.account_locked':
      return `${entry.personName ?? 'An account'} was locked after too many wrong passwords`;
    case 'auth.account_unlocked':
      return `${actor} unlocked ${personOf(entry)}`;
    case 'auth.session_revoked':
      return `${actor} ended ${personOf(entry)}'s sessions`;
    case 'auth.2fa_enrolled':
      return `${actor} set up two-factor sign-in`;
    case 'audit.viewed':
      return `${actor} opened the access log`;
    case 'audit.exported':
      return `${actor} exported the access log`;
    default:
      return `${actor}: ${entry.eventType}`;
  }
}

// --- Tone ---------------------------------------------------------------------------------------

export type EntryTone = 'urgent' | 'hold' | 'done' | 'new' | 'progress' | 'live';

/** Refused and failed are urgent; viewing as someone is on hold; role changes done; cover new; the rest calm. */
export function toneOf(entry: AuditEntry): EntryTone {
  if (entry.eventType === 'auth.account_locked') return 'urgent';
  switch (entry.category) {
    case 'failed_sign_in':
    case 'permission_denied':
      return 'urgent';
    case 'impersonation':
      return 'hold';
    case 'role_change':
      return 'done';
    case 'delegation':
      return 'new';
    case 'sign_in':
      return 'live';
    default:
      return 'progress';
  }
}

/** The chip on the right: the kind's own name, singular. */
export function kindLabelOf(category: AuditCategory | null): string {
  if (!category) return 'Entry';
  const label = AUDIT_CATEGORY_LABELS[category];
  return label.endsWith('s') ? label.slice(0, -1) : label;
}

// --- Days ---------------------------------------------------------------------------------------

export interface DayGroup<T> {
  /** `YYYY-MM-DD` in the organisation's zone. */
  readonly day: string;
  /** "Today · 27-10-2026", "Yesterday · 26-10-2026", "Friday · 23-10-2026". */
  readonly label: string;
  readonly entries: readonly T[];
}

function dayOf(value: Date | string, timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(value));
}

function weekdayOf(value: string, timeZone: string): string {
  return new Intl.DateTimeFormat('en-GB', { timeZone, weekday: 'long' }).format(new Date(value));
}

/** The feed's rows under one heading per day, in the order they came (newest first). */
export function groupByDay<T extends { readonly occurredAt: string }>(
  entries: readonly T[],
  now: Date = new Date(),
  timeZone: string = displayTimeZone(),
): readonly DayGroup<T>[] {
  const today = dayOf(now, timeZone);
  const yesterday = dayOf(new Date(now.getTime() - 24 * 60 * 60 * 1000), timeZone);
  const groups: { day: string; label: string; entries: T[] }[] = [];
  for (const entry of entries) {
    const day = dayOf(entry.occurredAt, timeZone);
    const last = groups[groups.length - 1];
    if (last && last.day === day) {
      last.entries.push(entry);
      continue;
    }
    const name = day === today ? 'Today' : day === yesterday ? 'Yesterday' : weekdayOf(entry.occurredAt, timeZone);
    groups.push({ day, label: `${name} · ${formatDate(entry.occurredAt, { timeZone })}`, entries: [entry] });
  }
  return groups;
}

// --- Before → after -------------------------------------------------------------------------------

export interface ChangeLine {
  readonly label: string;
  readonly before: string;
  readonly after: string;
}

interface CellSnapshot {
  readonly level: PermissionLevel;
  readonly scope: string | null;
  readonly isReadOnly: boolean;
}

function snapshotOf(value: unknown): CellSnapshot | null {
  const record = recordOf(value);
  if (typeof record.level !== 'string') return null;
  return { level: record.level as PermissionLevel, scope: stringOf(record.scope), isReadOnly: record.isReadOnly === true };
}

/** "All · Change too", "Their jobs · Look only", "None". */
export function cellWords(cell: CellSnapshot | null, object: PermissionObject, roleKey: RoleKey | null): string {
  if (!cell) return 'None';
  const answers = answersOfCell(cell, object);
  if (answers.reach === 'none') return 'None';
  return `${reachWording(object, answers, roleKey)} · ${CELL_ACCESS_LABELS[answers.access]}`;
}

const FIELD_LABELS: Readonly<Record<string, string>> = Object.fromEntries(FIELD_RESTRICTIONS.map((field) => [field.field, field.label]));

function fieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? toSentenceCase(field.replace(/[._]/g, ' '));
}

function stringList(value: unknown): readonly string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

function assignmentWords(value: unknown, lookups: AccessLogLookups): string {
  if (typeof value === 'string') return value;
  const assignment = recordOf(value);
  const roleId = stringOf(assignment.roleId);
  const role = (roleId && lookups.roleLabelOf(roleId)) ?? 'A role';
  const places = [...stringList(assignment.departmentIds), ...stringList(assignment.ventureIds)]
    .map((id) => lookups.placeNameOf(id))
    .filter((name): name is string => name !== null);
  const kind = stringOf(assignment.scopeKind) as RoleScopeKind | null;
  const reach = places.length > 0 ? places.join(', ') : kind && kind in ROLE_SCOPE_KIND_LABELS ? ROLE_SCOPE_KIND_LABELS[kind] : 'Whole organisation';
  return `${role} — ${reach}`;
}

function assignmentsWords(value: unknown, lookups: AccessLogLookups): string {
  const list = Array.isArray(value) ? value : value === undefined || value === null ? [] : [value];
  return list.length === 0 ? 'None' : list.map((item) => assignmentWords(item, lookups)).join('; ');
}

function dateRangeWords(record: Record_): string {
  const starts = stringOf(record.startsAt);
  const ends = stringOf(record.endsAt);
  if (!starts && !ends) return EMPTY;
  return `${starts ? formatDate(starts) : EMPTY} to ${ends ? formatDate(ends) : EMPTY}`;
}

function plainValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return EMPTY;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  return EMPTY;
}

function humanise(key: string): string {
  return toSentenceCase(key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' '));
}

/** What exactly changed, before → after, in plain names. Empty when the entry is not a change. */
export function changesOf(entry: AuditEntry, lookups: AccessLogLookups = NO_LOOKUPS): readonly ChangeLine[] {
  const before = recordOf(entry.payloadBefore);
  const after = recordOf(entry.payloadAfter);
  const roleKey = entry.roleId ? lookups.roleKeyOf(entry.roleId) : null;

  switch (entry.eventType) {
    case 'role.updated': {
      const lines: ChangeLine[] = [];
      if ('label' in after) lines.push({ label: 'Name', before: plainValue(before.label), after: plainValue(after.label) });
      if ('description' in after) lines.push({ label: 'Description', before: plainValue(before.description), after: plainValue(after.description) });
      const beforeCells = recordOf(before.cells);
      for (const [object, cell] of Object.entries(recordOf(after.cells))) {
        if (!isPermissionObject(object)) continue;
        const was = cellWords(snapshotOf(beforeCells[object]), object, roleKey);
        const now = cellWords(snapshotOf(cell), object, roleKey);
        // A cell whose only change was its hidden fields is told by its own entry (`role.field_rule_changed`).
        if (was !== now) lines.push({ label: PERMISSION_OBJECT_COPY[object].label, before: was, after: now });
      }
      return lines;
    }
    case 'role.field_rule_changed': {
      const wasHidden = new Set(Object.values(recordOf(before.cells)).flatMap(stringList));
      const nowHidden = new Set(Object.values(recordOf(after.cells)).flatMap(stringList));
      const fields = [...new Set([...wasHidden, ...nowHidden])].filter((field) => wasHidden.has(field) !== nowHidden.has(field));
      return fields.map((field) => ({
        label: fieldLabel(field),
        before: wasHidden.has(field) ? 'Hidden' : 'Shown',
        after: nowHidden.has(field) ? 'Hidden' : 'Shown',
      }));
    }
    case 'role.transition_rule_changed': {
      const was = recordOf(before.transitions);
      return Object.entries(recordOf(after.transitions)).map(([key, isAllowed]) => ({
        label: RESTRICTED_TRANSITION_LABELS[key as RestrictedTransition] ?? humanise(key),
        before: was[key] === true ? 'Allowed' : 'Not allowed',
        after: isAllowed === true ? 'Allowed' : 'Not allowed',
      }));
    }
    case 'user.role_assigned':
      return [
        {
          label: 'Roles',
          before: assignmentsWords(before.assignments, lookups),
          after: assignmentsWords(after.assignments ?? after.assignment, lookups),
        },
      ];
    case 'delegation.dates_changed':
      return [{ label: 'Dates', before: dateRangeWords(before), after: dateRangeWords(after) }];
    default: {
      // Anything else that stores plain values before and after (a name, a manager switch): each changed one.
      if (entry.payloadBefore === null || entry.payloadAfter === null) return [];
      const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])];
      return keys
        .filter((key) => plainValue(before[key]) !== plainValue(after[key]))
        .filter((key) => plainValue(before[key]) !== EMPTY || plainValue(after[key]) !== EMPTY)
        .map((key) => ({ label: humanise(key), before: plainValue(before[key]), after: plainValue(after[key]) }));
    }
  }
}

// --- Who and where ------------------------------------------------------------------------------

export interface Fact {
  readonly label: string;
  readonly value: string;
  /** Monospaced: an address or a path. */
  readonly isCode?: boolean;
}

const FAILURE_REASONS: Readonly<Record<string, string>> = {
  bad_password: 'Wrong password',
  locked_out: 'Account locked after too many wrong passwords',
  unknown_email: 'No account with that email',
  status_invited: "Hasn't accepted their invite yet",
  status_deactivated: 'Account deactivated',
  status_suspended: 'Account suspended',
};

function roleKeyLabel(value: string): string {
  return (ROLE_KEYS as readonly string[]).includes(value) ? ROLE_LABELS[value as RoleKey] : toSentenceCase(value.replace(/[-_]/g, ' '));
}

/** The address and browser, `—` for an entry written before they were stored (2026-11-02). */
export function whereOf(entry: AuditEntry): { readonly address: string; readonly device: string } {
  return { address: entry.ip ?? EMPTY, device: describeDevice(entry.userAgent) ?? EMPTY };
}

/** The feed's second line: the change, the reason, the path, then the device and address. */
export function summaryOf(entry: AuditEntry, lookups: AccessLogLookups = NO_LOOKUPS): string {
  const metadata = metadataOf(entry);
  const bits: string[] = [];
  const [first, ...rest] = changesOf(entry, lookups);
  if (first) bits.push(`${first.label}: ${first.before} → ${first.after}${rest.length > 0 ? ` and ${rest.length} more` : ''}`);
  if (first && entry.roleHolderCount !== null && entry.category === 'role_change' && entry.eventType !== 'user.role_assigned') {
    bits.push(`affects ${entry.roleHolderCount} ${entry.roleHolderCount === 1 ? 'person' : 'people'}`);
  }
  const reason = stringOf(metadata.reason);
  if (reason) bits.push(FAILURE_REASONS[reason] ?? `Reason: "${reason}"`);
  const path = stringOf(metadata.path);
  if (path) bits.push(path);
  if (entry.viewedAsName && stringOf(metadata.impersonatorUserId)) bits.push(`while viewing as ${entry.viewedAsName}`);
  const where = whereOf(entry);
  if (where.device !== EMPTY) bits.push(where.device);
  if (where.address !== EMPTY) bits.push(where.address);
  return bits.length > 0 ? bits.join(' · ') : EMPTY;
}

/** Who did it and from where, then the details today's page showed: the path, the roles held, "while viewing as". */
export function factsOf(entry: AuditEntry, lookups: AccessLogLookups = NO_LOOKUPS): readonly Fact[] {
  const metadata = metadataOf(entry);
  const after = recordOf(entry.payloadAfter);
  const where = whereOf(entry);
  // Review R21: an attempt on somebody's account has no "Done by"; it names the account tried.
  const isAttempt = isAccountAttempt(entry.eventType);
  const facts: Fact[] = isAttempt
    ? [{ label: 'Account tried', value: entry.personName ?? stringOf(metadata.email) ?? EMPTY }]
    : [{ label: 'Done by', value: entry.actorName ?? (entry.actorType === 'system' ? 'The system' : EMPTY) }];

  if (stringOf(metadata.impersonatorUserId)) facts.push({ label: 'While viewing as', value: entry.viewedAsName ?? EMPTY });
  if (!isAttempt && entry.personName && entry.personName !== entry.actorName) facts.push({ label: 'About', value: entry.personName });
  if (entry.roleLabel) facts.push({ label: 'Role', value: entry.roleLabel });

  if (entry.eventType === 'auth.permission_denied') {
    const action = stringOf(metadata.action);
    facts.push({ label: 'Tried to', value: `${action === 'view' ? 'Look at' : 'Change'} ${objectLabel(metadata.object)}` });
  }
  const path = stringOf(metadata.path);
  if (path) facts.push({ label: 'Path', value: [stringOf(metadata.method), path].filter(Boolean).join(' '), isCode: true });
  const roles = stringList(metadata.roles);
  if (roles.length > 0) facts.push({ label: 'Roles held', value: roles.map(roleKeyLabel).join(', ') });

  const reason = stringOf(metadata.reason);
  if (reason) facts.push({ label: 'Reason', value: FAILURE_REASONS[reason] ?? reason });

  if (entry.eventType === 'role.created') {
    facts.push({ label: 'Started from', value: stringOf(after.clonedFrom) ? `A copy of ${String(after.clonedFrom)}` : 'Blank' });
  }
  if (entry.category === 'delegation') {
    const parties = { ...recordOf(entry.payloadBefore), ...after };
    const away = stringOf(parties.fromUserId);
    const cover = stringOf(parties.toUserId);
    if (away) facts.push({ label: 'Away', value: lookups.personNameOf(away) ?? EMPTY });
    if (cover) facts.push({ label: 'Covering', value: lookups.personNameOf(cover) ?? EMPTY });
    if (entry.eventType === 'delegation.created') facts.push({ label: 'Dates', value: dateRangeWords(after) });
    const coverReason = stringOf(after.reason);
    if (coverReason) facts.push({ label: 'Reason', value: coverReason });
  }

  facts.push({ label: 'Device', value: where.device });
  facts.push({ label: 'Address', value: where.address, isCode: where.address !== EMPTY });
  return facts;
}
